import { useState, useRef, useCallback, useEffect, useLayoutEffect, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** Tooltip content (typically the full text of a truncated label). */
  label: ReactNode;
  /** The trigger element the tooltip describes. */
  children: ReactNode;
  /**
   * Preferred side. Used by `anchored` tooltips (the bubble sits on that side of the trigger,
   * flipping when there is no room); a cursor-following tooltip ignores it.
   */
  side?: Side;
  /**
   * Pin the bubble to the TRIGGER instead of following the cursor — for toolbar buttons, where
   * the tooltip should read as "this button's label". Placed on `side`, flipped to the opposite
   * side (then the remaining two) when it would leave the viewport, clamped as a last resort.
   */
  anchored?: boolean;
  /** Hover delay before an anchored tooltip shows, ms — so sweeping across a toolbar is quiet. */
  delay?: number;
  /** Extra classes for the inline-flex wrapper around the trigger. */
  className?: string;
  /** Skip the tooltip entirely (e.g. when the label isn't actually truncated). */
  disabled?: boolean;
  /** Max bubble width in px (long text wraps). */
  maxWidth?: number;
}

const GAP = 14; // distance from the cursor
const ANCHOR_GAP = 6; // distance from the trigger, anchored mode
const MARGIN = 8; // min distance from any viewport edge

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Where a bubble of size w×h goes on `side` of rect r, and whether it fits there. */
function placeOn(side: Side, r: DOMRect, w: number, h: number, vw: number, vh: number) {
  let left: number;
  let top: number;
  if (side === 'top' || side === 'bottom') {
    left = r.left + r.width / 2 - w / 2;
    top = side === 'top' ? r.top - ANCHOR_GAP - h : r.bottom + ANCHOR_GAP;
  } else {
    top = r.top + r.height / 2 - h / 2;
    left = side === 'left' ? r.left - ANCHOR_GAP - w : r.right + ANCHOR_GAP;
  }
  const fits = side === 'top' ? top >= MARGIN
    : side === 'bottom' ? top + h <= vh - MARGIN
      : side === 'left' ? left >= MARGIN
        : left + w <= vw - MARGIN;
  // Slide along the edge it sits on so it never pokes past the viewport.
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
  return { left, top, fits };
}

/**
 * App-wide themed tooltip. Replaces the browser's default `title` hover (which can't be
 * styled and looks out of place). Rendered in a portal (so `overflow-hidden` parents / tables
 * never clip it), and measured then kept fully on-screen. Colours come from the project theme
 * CSS variables, so it matches light & dark in both the local and cloud apps.
 *
 * Two modes: by default the bubble follows the mouse pointer; `anchored` pins it to a side of
 * the trigger (see the prop). A disabled button inside still gets its tooltip — disabled
 * controls swallow mouse events, so the wrapper takes the hover for them.
 */
export default function Tooltip({
  label,
  children,
  side = 'top',
  anchored = false,
  delay = 250,
  className = '',
  disabled = false,
  maxWidth = 320,
}: TooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);
  const showTimer = useRef<number | undefined>(undefined);
  const off = disabled || label == null || label === '';

  // Follow the cursor so the tooltip sits right beside the pointer (updates as it moves).
  // Anchored: the pointer only decides WHEN (after `delay`), the trigger decides where.
  const track = useCallback((e: ReactMouseEvent) => {
    if (off) return;
    if (!anchored) { setAnchor({ x: e.clientX, y: e.clientY }); return; }
    if (showTimer.current !== undefined) return;
    showTimer.current = window.setTimeout(() => setAnchor({ x: 0, y: 0 }), delay);
  }, [off, anchored, delay]);

  // Keyboard focus has no cursor → anchor to the trigger's bottom edge (or its side, anchored).
  const onFocus = useCallback(() => {
    if (off) return;
    const r = ref.current?.getBoundingClientRect();
    if (r) setAnchor({ x: r.left + r.width / 2, y: r.bottom });
  }, [off]);

  const hide = useCallback(() => {
    window.clearTimeout(showTimer.current);
    showTimer.current = undefined;
    setAnchor(null);
    setBox(null);
  }, []);
  useEffect(() => () => window.clearTimeout(showTimer.current), []);
  // A click usually changes what the button means (play → pause) or opens something: hide.
  // An anchored tooltip whose trigger unmounts or is disabled mid-hover must not linger.
  useEffect(() => { if (off) hide(); }, [off, hide]);

  // Measure the rendered bubble, then place it and keep it on-screen.
  useLayoutEffect(() => {
    if (!anchor || !bubbleRef.current) return;
    const b = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (anchored && ref.current) {
      // The trigger itself, not the wrapper: an absolutely positioned button (a tile's corner
      // control) leaves the inline wrapper zero-sized somewhere else entirely.
      const r = (ref.current.firstElementChild ?? ref.current).getBoundingClientRect();
      const order: Side[] = [side, OPPOSITE[side], ...(['top', 'bottom', 'left', 'right'] as Side[]).filter((s) => s !== side && s !== OPPOSITE[side])];
      const best = order.map((s) => placeOn(s, r, b.width, b.height, vw, vh)).find((p) => p.fits)
        ?? placeOn(side, r, b.width, b.height, vw, vh);
      setBox({ left: best.left, top: best.top });
      return;
    }

    let left = anchor.x + GAP;
    if (left + b.width > vw - MARGIN) left = anchor.x - GAP - b.width; // flip to the left of cursor
    left = Math.max(MARGIN, Math.min(left, vw - b.width - MARGIN));

    let top = anchor.y + GAP;
    if (top + b.height > vh - MARGIN) top = anchor.y - GAP - b.height;  // flip above cursor
    top = Math.max(MARGIN, Math.min(top, vh - b.height - MARGIN));

    setBox({ left, top });
  }, [anchor, anchored, side]);

  return (
    <span
      ref={ref}
      className={`inline-flex [&>*:disabled]:pointer-events-none ${className}`}
      onMouseEnter={track}
      onMouseMove={track}
      onMouseLeave={hide}
      onMouseDown={anchored ? hide : undefined}
      onFocus={onFocus}
      onBlur={hide}
    >
      {children}
      {anchor && !off && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          className="pointer-events-none fixed z-[9999] rounded-md px-2 py-1 text-xs font-medium leading-snug break-words"
          style={{
            // Off-screen until measured (avoids a one-frame flash at the wrong spot).
            left: box ? box.left : -9999,
            top: box ? box.top : -9999,
            opacity: box ? 1 : 0,
            maxWidth,
            width: 'max-content',
            background: 'var(--popover)',
            color: 'var(--popover-foreground)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
          }}
        >
          {label}
        </div>,
        document.body,
      )}
    </span>
  );
}

/**
 * A toolbar button's tooltip: `Tooltip` pinned to the trigger (anchored), above it by default,
 * flipping to whichever side has room. Use in place of `title=` on icon buttons.
 */
export function Tip(props: Omit<TooltipProps, 'anchored'>) {
  return <Tooltip anchored {...props} />;
}
